import lodash from "lodash";
import { Tree, TreeNode } from "@/types/tree";
import { Control } from "@/types/controls";
import { configStore, documentStore } from "@/store/store";
import {
  FieldToComplete,
  CompletedField,
  PromptInfo,
  UnsplashImage,
  FieldToFill
} from "@/lib/ai/types";
import { numberToOrdinal } from "@/lib/ai/prompts";

import fpJsonParse from "@/util/fp-json-parse";
import { pipe } from "fp-ts/lib/function";
import { fold } from "fp-ts/lib/Either";
import { findNode, getNodeIndex, getOutermostAncestor } from "../tree/tree";
import { SECTION_SLUG } from "@/lib/elements/constants";
import defensiveCopy from "@/util/defensive-copy";

const emptyCompletion = {
  elements: [],
  unsplash_keywords: []
};

export function processCompletion(
  maybeJsonCompletion: string | undefined,
  fieldsToComplete: FieldToComplete[]
): {
  elements: CompletedField[];
  unsplash_keywords: string[];
} {
  if (!maybeJsonCompletion) {
    return emptyCompletion;
  }

  const output = pipe(
    fpJsonParse(maybeJsonCompletion),
    fold(
      errors => {
        console.log("Error parsing JSON from AI:", errors[0].msg);
        console.log(maybeJsonCompletion);
        console.log("");
        return null;
      },
      completion => completion
    )
  ) as {
    elements: FieldToFill[];
    unsplash_keywords: string[];
  };

  if (!output) {
    return emptyCompletion;
  }

  if (!Array.isArray(output?.elements)) {
    return emptyCompletion;
  }

  return {
    ...output,
    elements: output.elements
      .filter(e => {
        // Remove elements that don't have an index and content set.
        // This usually happens when the AI hasn't finished generating the completion.
        return e.index !== undefined && e.content !== undefined;
      })
      .map(g => ({
        nodeId: fieldsToComplete[g.index].nodeId,
        controlPath: fieldsToComplete[g.index].controlPath,
        value: g.content
      }))
  };
}

let lastFields: CompletedField[] = [];

function isSameField(a: CompletedField, b: CompletedField) {
  return (
    a.nodeId === b.nodeId &&
    a.controlPath === b.controlPath &&
    a.value === b.value
  );
}

export function updateDocument(fields: CompletedField[]) {
  const newFields = fields.filter(
    f => !lastFields.some(lf => isSameField(lf, f))
  );

  lastFields = fields;

  newFields.forEach(v => {
    documentStore.throttledPropertyChanged({
      elementId: v.nodeId,
      path: v.controlPath,
      value: v.value,
      meta: {
        snapshotLabel: "Update from Generative AI"
      }
    });
  });
}

export function updateImages(
  imagesFields: FieldToComplete[],
  unsplashResults: UnsplashImage[],
  fillEmptyOnly: boolean
) {
  imagesFields
    .filter(i => {
      if (fillEmptyOnly) {
        const maybeNode = findNode(documentStore.document.tree, i.nodeId);
        if (!maybeNode) return false;
        const currentValue = lodash.get(
          maybeNode.data.properties,
          i.controlPath
        );

        return (
          currentValue === null ||
          currentValue === undefined ||
          currentValue === ""
        );
      }

      return true;
    })
    .forEach((image, index) => {
      const img = unsplashResults[index];

      documentStore.unthrottledPropertyChanged({
        elementId: image.nodeId,
        path: image.controlPath,
        value: {
          id: -1,
          type: "external_image",
          url: img.urls.regular,
          alt: img.alt_description || "",
          caption: img.description || ""
        },
        meta: {
          snapshotLabel: "Update image from Generative AI"
        }
      });
    });
}

// TODO: Refactor to a functional style

export function getFieldsToComplete(
  node: TreeNode,
  acc: FieldToComplete[] = []
): FieldToComplete[] {
  const element = configStore.element(node.data.type);

  if (element.settings && element.settings.disableAI) return acc;

  let acc_: FieldToComplete[] = [
    ...acc,
    ...getFieldsToCompleteFromControls(
      element.controls.contentSections,
      "content",
      node
    )
  ];

  node.children.forEach(n => {
    acc_ = getFieldsToComplete(n, acc_);
  });

  return acc_;
}

export function getImagesToComplete(
  node: TreeNode,
  acc: FieldToComplete[] = []
): FieldToComplete[] {
  let acc_: FieldToComplete[] = [
    ...acc,
    ...getImagesToCompleteFromControls(
      configStore.element(node.data.type).controls.contentSections,
      "content",
      node
    )
  ];

  node.children.forEach(n => {
    acc_ = getImagesToComplete(n, acc_);
  });

  return acc_;
}

function getImagesToCompleteFromControls(
  controls: Control[],
  path: string,
  node: TreeNode,
  acc: FieldToComplete[] = []
) {
  const element = configStore.element(node.data.type);
  const _acc = acc;

  controls.forEach(control => {
    const controlPath = `${path}.${control.slug}`;

    if (control.options.type === "wpmedia") {
      _acc.push({
        nodeId: node.id,
        nodeName: element.name,
        controlPath: controlPath,
        field: {
          description: getFieldDescription(
            control,
            controlPath,
            node.data.type
          ),
          label: control.label,
          value: lodash.get(node.data.properties, controlPath),
          type: control.options.type,
          locked: isFieldLocked(node, controlPath)
        }
      });
    }

    getImagesToCompleteFromControls(control.children, controlPath, node, _acc);
  });

  return _acc;
}

function getFieldsToCompleteFromControls(
  controls: Control[],
  path: string,
  node: TreeNode,
  acc: FieldToComplete[] = []
) {
  const element = configStore.element(node.data.type);
  const _acc = acc;

  controls.forEach(control => {
    const controlPath = `${path}.${control.slug}`;

    if (isControlSupported(control) && !isValueEmpty(control, path, node)) {
      _acc.push({
        nodeId: node.id,
        controlPath: controlPath,
        nodeName: element.name,
        field: {
          description: getFieldDescription(
            control,
            controlPath,
            node.data.type
          ),
          label: control.label,
          value: lodash.get(node.data.properties, controlPath),
          type: control.options.type,
          locked: isFieldLocked(node, controlPath)
        }
      });
    }

    // Ignore controls inside repeaters
    if (control.options.type === "repeater") return;

    getFieldsToCompleteFromControls(control.children, controlPath, node, _acc);
  });

  return _acc;
}

function isValueEmpty(control: Control, path: string, node: TreeNode) {
  const controlPath = `${path}.${control.slug}`;
  const value = lodash.get(node.data.properties, controlPath, "");
  return value === "" || value === null || value === undefined;
}

function isControlSupported(control: Control) {
  return ["text", "richtext", "repeater"].includes(control.options.type);
}

function getFieldDescription(
  control: Control,
  controlPath: string,
  elementType: string
) {
  const elementName = configStore.element(elementType).name;

  if (control.options.type === "repeater") {
    const childControls = control.children
      .filter(isControlSupported)
      .map(childControl => childControl.slug);

    const keys = childControls.join(", ");
    return `${elementName} - An array of objects with the following keys: ${keys}.`;
  }

  if (controlPath.endsWith("button.text")) {
    return `${elementName} - Button`;
  }

  if (elementName === control.label) {
    return elementName;
  }

  return `${elementName} - ${control.label}`;
}

export function generateElementDescription(
  tree: Tree,
  node: TreeNode,
  postTitle: string,
  postType: string
) {
  const elementName = configStore.element(node.data.type).name;
  const nodeIndex = getNodeIndex(tree, node.id) as number;
  const parent = getOutermostAncestor(tree, node.id);
  const parentName = parent ? configStore.element(parent.data.type).name : "";
  const parts = [];

  if (parent) {
    const parentIndex = getNodeIndex(tree, parent.id) as number;
    const ordinal = numberToOrdinal(parentIndex + 1);

    parts.push(elementName);
    parts.push("on");
    parts.push(ordinal);
    parts.push(parentName);
  } else if (node.data.type === SECTION_SLUG) {
    const ordinal = numberToOrdinal(nodeIndex + 1);
    parts.push(nodeIndex === 0 ? "Hero" : ordinal);
    parts.push("section");
  }

  parts.push("on");
  parts.push(`"${postTitle}"`);

  parts.push(postType.replace("breakdance_", ""));

  return parts.join(" ");
}

export function getElementFriendlyName(node: TreeNode) {
  const friendlyName = node.data?.properties?.meta?.friendlyName;
  if (friendlyName) return friendlyName;
  const elementName = configStore.element(node.data.type).name;
  return `${elementName} #${node.id}`;
}

export function getElementPrompt(
  node: TreeNode
): Partial<PromptInfo> | undefined {
  return node.data?.properties?.meta?.promptInfo;
}

export function getElementDescription(node: TreeNode) {
  const doc = documentStore.document.documentMeta;
  const metadata = getElementPrompt(node);

  // Get description saved in element metadata
  if (metadata?.sectionDescription) {
    return metadata.sectionDescription;
  }

  // Otherwise, generate a default description
  return generateElementDescription(
    documentStore.document.tree,
    node,
    doc.titleLabel,
    doc.postType
  );
}

export function getElementQualifier(node: TreeNode) {
  const metadata = getElementPrompt(node);
  return metadata?.qualifiers || "";
}

export function getElementTemperature(node: TreeNode) {
  const temperature = getElementPrompt(node)?.temperature;
  return temperature !== undefined ? temperature : 1;
}

export function saveToElementMetadata(node: TreeNode, promptInfo: PromptInfo) {
  const elementName = configStore.element(node.data.type).name;

  documentStore.unthrottledPropertyChanged({
    elementId: node.id,
    path: "meta.promptInfo",
    meta: {
      snapshotLabel: `Updated prompt info for element "${elementName}"`
    },
    value: promptInfo
  });
}

export function lockFieldFromAI(node: TreeNode, fieldPath: string) {
  const elementName = configStore.element(node.data.type).name;

  const lockedFields = getElementPrompt(node)?.lockedFields || [];
  const newLockedFields = defensiveCopy(lockedFields);

  if (!newLockedFields.includes(fieldPath)) {
    newLockedFields.push(fieldPath);
  }

  documentStore.unthrottledPropertyChanged({
    elementId: node.id,
    path: `meta.promptInfo.lockedFields`,
    meta: {
      snapshotLabel: `Locked field "${fieldPath}" for element "${elementName}"`
    },
    value: newLockedFields
  });
}

export function unlockFieldFromAI(node: TreeNode, fieldPath: string) {
  const elementName = configStore.element(node.data.type).name;

  const lockedFields = getElementPrompt(node)?.lockedFields || [];
  const newLockedFields = defensiveCopy(lockedFields);

  const index = newLockedFields.indexOf(fieldPath);
  if (index > -1) {
    newLockedFields.splice(index, 1);
  }

  documentStore.unthrottledPropertyChanged({
    elementId: node.id,
    path: `meta.promptInfo.lockedFields`,
    meta: {
      snapshotLabel: `Unlocked field "${fieldPath}" for element "${elementName}"`
    },
    value: newLockedFields
  });
}

export function toggleFieldLock(node: TreeNode, fieldPath: string) {
  if (isFieldLocked(node, fieldPath)) {
    unlockFieldFromAI(node, fieldPath);
  } else {
    lockFieldFromAI(node, fieldPath);
  }
}

export function isFieldLocked(node: TreeNode, fieldPath: string) {
  const lockedFields = getElementPrompt(node)?.lockedFields || [];
  return lockedFields.includes(fieldPath);
}

export function getLockedFieldsPath(node: TreeNode) {
  return getElementPrompt(node)?.lockedFields || [];
}
